import type { WebShopOrderFilterChoices } from 'Services/SalesManagement/Features/Order/types'
import type { ListResponse } from 'Services/SalesManagement/types'
import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunkWithParams,
  type DynamicUrl,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

const initialState: OrderState = {
  webShopOrders: [],
  mobileOrders: [],
  walttiPosOrders: [],
  count: 0,
  next: null,
  previous: null,
  loading: 'idle',
  filteredSearchOptionsLoading: 'idle',
  deliverLoading: 'idle',
  refundLoading: 'idle',
  walletTransactionRefundLoading: 'idle',
  refundOptionsLoading: 'idle',
  refundHistoryLoading: 'idle',
  webShopOrderLineLogLoading: 'idle',
  webShopOrderReceiptLoading: 'idle',
  affiliateOrderReceiptLoading: 'idle',
  singleWebShopOrder: null,
  singleMobileOrder: null,
  singleWalttiPosOrder: null,
  webShopOrderFilterChoices: {
    authorities: [],
    payment_method_id: [],
    status: [],
    delivery_status: []
  },
  webShopOrderLineLog: []
}

interface ProductBenefitShape {
  applied_amount: number
  description: string
  reservation_status: string
}

export interface WalletTransaction {
  id: string
  rolled_back: boolean
}

interface WalletBenefitShape {
  applied_amount: number
  transaction: WalletTransaction | null
}

export type WebShopOrderPaymentMethodType =
  | '1_paytrail_payment'
  | 'paytrail'
  | 'afterpay'
  | 'benefits'
  | 'epassi'
  | 'netaxept'
  | 'netaxept_recurring'

export interface PaytrailPaymentEventShape {
  id: number
  description: string
  created: Date
}

export interface AfterpayPaymentEventShape {
  id: number
  description: string
  step: string
  created: Date
}

export interface NetaxeptPaymentEventShape {
  transaction_id: string | null
  created: Date
}

export interface NetaxeptRecurringPaymentEventShape {
  transaction_id: string
  created: Date
  status: string
}

export interface EpassiPaymentEventShape {
  id: number
  type: string
  created: Date
  description: string
}

export type BenefitPaymentEventShape = Record<string, never>

export interface WebShopOrderLineShape {
  id: number
  travel_card_number: string
  product_name: string
  vat_exclusive_price: number
  vat_amount: number
  vat_percentage: number
  discount_percent: number | null
  discount_codes: []
  amount: number | null
  status: string
  delivery_message: string
  product_benefit: ProductBenefitShape | Record<string, never>
  wallet_benefit: WalletBenefitShape | Record<string, never>
}

export interface WebShopOrderShape {
  id: number
  contact_email: string
  created: Date
  payment_date: Date
  payment_method_name: string | null
  status: string
  transactional_id: string
  authority_code: string
  authority_name: string
  note: [number, string]
}

export interface CommissionShape {
  created: Date
  id: number
  model: number
  modified: Date
  value: string
  vat_percentage: string
}

export interface WebShopOrderDetailShape extends WebShopOrderShape {
  commission: CommissionShape | null
  payment_method_name: string
  payment_method_type: WebShopOrderPaymentMethodType
  order_lines: WebShopOrderLineShape[]
  payment_events:
    | PaytrailPaymentEventShape[]
    | AfterpayPaymentEventShape[]
    | BenefitPaymentEventShape[]
    | NetaxeptPaymentEventShape[]
    | NetaxeptRecurringPaymentEventShape[]
    | EpassiPaymentEventShape[]
}

export interface WebShopOrderLineLogShape {
  created: Date
  message: string
  object_id: number
}

export interface OrderState {
  webShopOrders: WebShopOrderShape[]
  mobileOrders: MobileOrderShape[]
  walttiPosOrders: WalttiPosOrderShape[]
  count: number
  next: string | null
  previous: string | null
  loading: LoadingStates
  filteredSearchOptionsLoading: LoadingStates
  deliverLoading: LoadingStates
  refundLoading: LoadingStates
  walletTransactionRefundLoading: LoadingStates
  refundOptionsLoading: LoadingStates
  refundHistoryLoading: LoadingStates
  webShopOrderLineLogLoading: LoadingStates
  webShopOrderReceiptLoading: LoadingStates
  affiliateOrderReceiptLoading: LoadingStates
  singleWebShopOrder: WebShopOrderDetailShape | null
  singleMobileOrder: MobileOrderDetailShape | null
  singleWalttiPosOrder: WalttiPosOrderDetailShape | null
  webShopOrderFilterChoices: WebShopOrderFilterChoices
  webShopOrderLineLog: WebShopOrderLineLogShape[]
}

type FetchWebShopOrderResponse = WebShopOrderDetailShape

export interface WebShopOrderParamShape {
  page: number
  ordering: string
  search: string
  status: string
  authority_id: string
  payment_method_id: string
  delivery_status: string
  created_after: string
  created_before: string
}

const fetchWebShopOrder = createAxiosAsyncThunkWithParams<FetchWebShopOrderResponse, DynamicUrl>(
  'webShopOrder/fetchWebShopOrder',
  '/api/v1/web-orders'
)

const fetchWebShopOrderList = createAxiosAsyncThunkWithParams<
  ListResponse<WebShopOrderShape>,
  WebShopOrderParamShape | undefined
>('webShopOrder/fetchWebShopOrderList', '/api/v1/web-orders/')

type DeliverWebShopOrderResponse = Record<string, never>
interface DeliverWebShopOrderRequest {
  id: number
}

const deliverWebShopOrder = createAsyncThunk<
  DeliverWebShopOrderResponse,
  DeliverWebShopOrderRequest,
  {
    rejectValue: ValidationErrors
  }
>('webShopOrder/deliverWebShopOrder', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance().post<DeliverWebShopOrderResponse>(
      `/api/v1/web-orders/${id}/deliver/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({
        ...error.response.data,
        status: error.response.status
      })
    }
    throw err
  }
})

interface FetchWebShopOrderLineLogRequest {
  id: number
  line_pk: number
}

type FetchWebShopOrderLineLogResponse = WebShopOrderLineLogShape[]

const fetchWebShopOrderLineLog = createAsyncThunk<
  FetchWebShopOrderLineLogResponse,
  FetchWebShopOrderLineLogRequest,
  {
    rejectValue: ValidationErrors
  }
>('webShopOrder/fetchWebShopOrderLineLog', async (data, { rejectWithValue }) => {
  try {
    const { id, line_pk } = data
    const response = await getAxiosInstance().get<FetchWebShopOrderLineLogResponse>(
      `/api/v1/web-orders/${id}/order-lines/${line_pk}/log/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({
        ...error.response.data,
        status: error.response.status
      })
    }
    throw err
  }
})

interface RollbackWalletTransactionRequest {
  id: string
}

type RollbackWalletTransactionResponse = Record<string, never>

const rollbackWalletTransaction = createAsyncThunk<
  RollbackWalletTransactionResponse,
  RollbackWalletTransactionRequest,
  {
    rejectValue: ValidationErrors
  }
>('wallets/rollbackWalletTransaction', async (data, { rejectWithValue }) => {
  try {
    const { id, ...fields } = data
    const response = await getAxiosInstance().post<RollbackWalletTransactionResponse>(
      `/api/v1/wallets/transaction/${id}/rollback/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({
        ...error.response.data,
        status: error.response.status
      })
    }
    throw err
  }
})

interface FetchWebShopOrderReceiptRequest {
  id: number
}

type FetchWebShopOrderReceiptResponse = Blob

const fetchWebShopOrderReceipt = createAsyncThunk<
  FetchWebShopOrderReceiptResponse,
  FetchWebShopOrderReceiptRequest,
  {
    rejectValue: ValidationErrors
  }
>('webShopOrder/fetchWebShopOrderReceipt', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance('blob').get<FetchWebShopOrderReceiptResponse>(
      `/api/v1/web-orders/${id}/receipt/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError = err
      if (!error.response) {
        throw err
      }
      // Here we have a different kind of error handling because the responseType is expected to be a blob
      // but if the file generation fails in the backend, it returns a JSON response instead.
      const responseData = await (error.response.data as Blob).text() // .text() will convert the blob to a human-readable error response
      const responseJson = JSON.parse(responseData) // Convert the text into a JSON so that the error message can be better handled
      return rejectWithValue({
        ...responseJson,
        status: error.response.status
      })
    }
    throw err
  }
})

/** Mobile **/

export interface MobileOrderShape {
  id: number
  external_id: string
  operator_name: string
  sales_channel_name: string
  waltti_id: string
  created: Date
  payment_method: number
  payment_status: string | null
  payment_cell_operator: string | null
  order_value: string
}

export interface MobileOrderDetailShape {
  id: number
  external_id: string
  affiliate_identity_id: string
  travel_card_numbers: string[]
  waltti_user: string
  user_email: string
  operator_name: string
  sales_channel_name: string
  client_application_version: string
  created: Date
  product_scheme: string
  receipt_information: string | null
  ticket_order_lines: MobileTicketOrderLineShape[]
  payment_method: AffiliatePaymentMethodShape | null
  refund_requested: boolean
  refunded_by: RefundedByOption | null
}

export type AffiliateOrderPaymentMethodType = 'mobile' | 'bank_card' | 'mobilepay' | 'benefits'

export interface AffiliatePaymentMethodShape {
  created: Date
  transaction_id: string | null
  payment_operator: string | null
  card_pan: string | null
  payment_method_type: AffiliateOrderPaymentMethodType
  payment_method_name: string | null
  events: AffiliatePaymentEventShape[]
}

export interface AffiliatePaymentEventShape {
  external_timestamp: Date
  status: string
  note: string | null
}

export interface RefundSummaryShape {
  refund_available: boolean
  status: {
    text: string
  }
  note: string | null
}

export interface MobileTicketOrderLineShape {
  id: number
  external_id: string
  refund_summary: RefundSummaryShape
  ticket: {
    ticket_id: string
    product_id: string
    product_name: string
    customer_group: string
    external_receival_date: Date
    external_creation_date: Date
    external_relay_date: Date
    municipality: string
    start_zone: string
    end_zone: string
    valid_from: Date
    valid_to: Date
    product_expiration_date: Date
    vat_exclusive_price: number
    vat_amount: number
    price: string
  }
}

type FetchMobileOrderResponse = MobileOrderDetailShape

const fetchMobileOrder = createAxiosAsyncThunkWithParams<FetchMobileOrderResponse, DynamicUrl>(
  'mobileOrder/fetchMobileOrder',
  '/api/v1/affiliate-orders/mobile'
)

export interface MobileOrderParamShape {
  page: number
  ordering: string
  search: string
  search_field: string
  operator_id: string
  sales_channel_name: string
  payment_method: string
  refund_status: string
  created_after: string
  created_before: string
}

const fetchMobileOrderList = createAxiosAsyncThunkWithParams<
  ListResponse<MobileOrderShape>,
  MobileOrderParamShape | undefined
>('mobileOrder/fetchMobileOrderList', '/api/v1/affiliate-orders/mobile/')

type RequestMobileOrderRefundResponse = Record<string, never>

interface RequestMobileOrderRefundRequest {
  orderId: number
  reason: string
  note?: string
}

const requestMobileOrderRefund = createAsyncThunk<
  RequestMobileOrderRefundResponse,
  RequestMobileOrderRefundRequest,
  {
    rejectValue: ValidationErrors
  }
>('mobileOrder/requestMobileOrderRefund', async (data, { rejectWithValue }) => {
  try {
    const { orderId, ...fields } = data
    const response = await getAxiosInstance().post<RequestMobileOrderRefundResponse>(
      `/api/v1/affiliate-orders/mobile/${orderId}/refund/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({
        ...error.response.data,
        status: error.response.status
      })
    }
    throw err
  }
})

interface RequestMobileOrderLineRefundRequest {
  orderId: number
  orderLineId: number
  reason: string
  note?: string
}

const requestMobileOrderLineRefund = createAsyncThunk<
  RequestMobileOrderRefundResponse,
  RequestMobileOrderLineRefundRequest,
  {
    rejectValue: ValidationErrors
  }
>('mobileOrder/requestMobileOrderLineRefund', async (data, { rejectWithValue }) => {
  try {
    const { orderId, orderLineId, ...fields } = data
    const response = await getAxiosInstance().post<RequestMobileOrderRefundResponse>(
      `/api/v1/affiliate-orders/mobile/${orderId}/refund-ticket-line/${orderLineId}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({
        ...error.response.data,
        status: error.response.status
      })
    }
    throw err
  }
})

interface FetchAffiliateOrderReceiptRequest {
  id: number
}

type FetchAffiliateOrderReceiptResponse = Blob

const fetchMobileOrderReceipt = createAsyncThunk<
  FetchAffiliateOrderReceiptResponse,
  FetchAffiliateOrderReceiptRequest,
  {
    rejectValue: ValidationErrors
  }
>('mobileOrder/fetchMobileOrderReceipt', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance('blob').get<FetchAffiliateOrderReceiptResponse>(
      `/api/v1/affiliate-orders/mobile/${id}/receipt/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError = err
      if (!error.response) {
        throw err
      }
      // Here we have a different kind of error handling because the responseType is expected to be a blob
      // but if the file generation fails in the backend, it returns a JSON response instead.
      const responseData = await (error.response.data as Blob).text() // .text() will convert the blob to a human-readable error response
      const responseJson = JSON.parse(responseData) // Convert the text into a JSON so that the error message can be better handled
      return rejectWithValue({ ...responseJson, status: error.response.status })
    }
    throw err
  }
})

/** WalttiPOS **/

export interface WalttiPosOrderShape {
  id: number
  external_id: string
  operator_name: string
  sales_channel_name: string
  waltti_id: string
  created: Date
  payment_method: number
  payment_status: string | null
  payment_cell_operator: string | null
  order_value: string | null
}

export enum RefundedByType {
  POS = 'pos',
  user = 'user'
}

export type RefundedByOption = RefundedByType.POS | RefundedByType.user

export interface WalttiPosOrderDetailShape {
  id: number
  external_id: string
  affiliate_identity_id: string
  travel_card_numbers: string[]
  waltti_user: string
  user_email: string
  operator_name: string
  sales_channel_name: string
  client_application_version: string
  created: Date
  product_scheme: string
  receipt_information: string | null
  ticket_order_lines: WalttiPosTicketOrderLineShape[]
  payment_method: AffiliatePaymentMethodShape | null
  refunded_by: RefundedByOption | null
  refund_requested: boolean
}

export interface AffiliatePaymentEventShape {
  external_timestamp: Date
  status: string
}

export interface WalttiPosTicketOrderLineShape {
  external_id: string
  id: number
  refund_summary: RefundSummaryShape
  ticket: {
    ticket_id: string
    product_id: string
    product_name: string
    customer_group: string
    external_receival_date: Date
    external_creation_date: Date
    external_relay_date: Date
    municipality: string
    start_zone: string
    end_zone: string
    valid_from: Date
    valid_to: Date
    product_expiration_date: Date
    vat_exclusive_price: number
    vat_amount: number
    price: string
  }
}

type FetchWalttiPosOrderResponse = WalttiPosOrderDetailShape

const fetchWalttiPosOrder = createAxiosAsyncThunkWithParams<
  FetchWalttiPosOrderResponse,
  DynamicUrl
>('walttiPosOrder/fetchWalttiPosOrder', '/api/v1/affiliate-orders/register-walttipos')

export interface WalttiPosOrderParamShape {
  page: number
  ordering: string
  search: string
  search_field: string
  operator_id: string
  sales_channel_name: string
  payment_method: string
  refund_status: string
  created_after: string
  created_before: string
}

const fetchWalttiPosOrderList = createAxiosAsyncThunkWithParams<
  ListResponse<WalttiPosOrderShape>,
  WalttiPosOrderParamShape | undefined
>('walttiPosOrder/fetchWalttiPosOrderList', '/api/v1/affiliate-orders/register-walttipos/')

const fetchWalttiPosOrderReceipt = createAsyncThunk<
  FetchAffiliateOrderReceiptResponse,
  FetchAffiliateOrderReceiptRequest,
  {
    rejectValue: ValidationErrors
  }
>('walttiPosOrder/fetchWalttiPosOrderReceipt', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance('blob').get<FetchAffiliateOrderReceiptResponse>(
      `/api/v1/affiliate-orders/register-walttipos/${id}/receipt/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError = err // cast the error for access
      if (!error.response) {
        throw err
      }
      // Here we have a different kind of error handling because the responseType is expected to be a blob
      // but if the file generation fails in the backend, it returns a JSON response instead.
      const responseData = await (error.response.data as Blob).text() // .text() will convert the blob to a human-readable error response
      const responseJson = JSON.parse(responseData) // Convert the text into a JSON so that the error message can be better handled
      return rejectWithValue({
        ...responseJson,
        status: error.response.status
      })
    }
    throw err
  }
})

export type AffiliateOrderShape = MobileOrderShape | WalttiPosOrderShape
export type AffiliateOrderParamShape = MobileOrderParamShape | WalttiPosOrderParamShape
export type AffiliateTicketOrderLineShape =
  | MobileTicketOrderLineShape
  | WalttiPosTicketOrderLineShape

const genericActions = actionGenerator('order')

const orderSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchWebShopOrder.fulfilled, (state, { payload }) => {
        state.singleWebShopOrder = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchWebShopOrder.pending, state => {
        state.singleWebShopOrder = initialState.singleWebShopOrder
        state.loading = 'pending'
      })
      .addCase(fetchWebShopOrder.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchWebShopOrderList.fulfilled, (state, { payload }) => {
        state.webShopOrders = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.singleWebShopOrder = initialState.singleWebShopOrder // Set the single order to its initial state so that the previously displayed single order won't be briefly rendered when displaying another single order
        state.loading = 'fulfilled'
      })
      .addCase(fetchWebShopOrderList.pending, state => {
        state.webShopOrders = initialState.webShopOrders
        state.count = initialState.count
        state.next = initialState.next
        state.previous = initialState.previous
        state.loading = 'pending'
      })
      .addCase(fetchWebShopOrderList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(deliverWebShopOrder.fulfilled, state => {
        state.deliverLoading = 'fulfilled'
      })
      .addCase(deliverWebShopOrder.pending, state => {
        state.deliverLoading = 'pending'
      })
      .addCase(deliverWebShopOrder.rejected, state => {
        state.deliverLoading = 'rejected'
      })

      .addCase(fetchWebShopOrderLineLog.fulfilled, (state, { payload }) => {
        state.webShopOrderLineLog = payload
        state.webShopOrderLineLogLoading = 'fulfilled'
      })
      .addCase(fetchWebShopOrderLineLog.pending, state => {
        state.webShopOrderLineLogLoading = 'pending'
        state.webShopOrderLineLog = initialState.webShopOrderLineLog
      })
      .addCase(fetchWebShopOrderLineLog.rejected, state => {
        state.webShopOrderLineLogLoading = 'rejected'
        state.webShopOrderLineLog = initialState.webShopOrderLineLog
      })

      .addCase(rollbackWalletTransaction.fulfilled, state => {
        state.walletTransactionRefundLoading = 'fulfilled'
      })
      .addCase(rollbackWalletTransaction.pending, state => {
        state.walletTransactionRefundLoading = 'pending'
      })
      .addCase(rollbackWalletTransaction.rejected, state => {
        state.walletTransactionRefundLoading = 'rejected'
      })

      .addCase(fetchWebShopOrderReceipt.fulfilled, state => {
        state.webShopOrderReceiptLoading = 'fulfilled'
      })
      .addCase(fetchWebShopOrderReceipt.pending, state => {
        state.webShopOrderReceiptLoading = 'pending'
      })
      .addCase(fetchWebShopOrderReceipt.rejected, state => {
        state.webShopOrderReceiptLoading = 'rejected'
      })

      /** Mobile **/
      .addCase(fetchMobileOrder.fulfilled, (state, { payload }) => {
        state.singleMobileOrder = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchMobileOrder.pending, state => {
        state.singleMobileOrder = initialState.singleMobileOrder
        state.loading = 'pending'
      })
      .addCase(fetchMobileOrder.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchMobileOrderList.fulfilled, (state, { payload }) => {
        state.mobileOrders = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.singleMobileOrder = initialState.singleMobileOrder // Set the single order to its initial state so that the previously displayed single order won't be briefly rendered when displaying another single order
        state.loading = 'fulfilled'
      })
      .addCase(fetchMobileOrderList.pending, state => {
        state.mobileOrders = initialState.mobileOrders
        state.count = initialState.count
        state.next = initialState.next
        state.previous = initialState.previous
        state.loading = 'pending'
      })
      .addCase(fetchMobileOrderList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(requestMobileOrderRefund.fulfilled, state => {
        state.refundLoading = 'fulfilled'
      })
      .addCase(requestMobileOrderRefund.pending, state => {
        state.refundLoading = 'pending'
      })
      .addCase(requestMobileOrderRefund.rejected, state => {
        state.refundLoading = 'rejected'
      })

      .addCase(requestMobileOrderLineRefund.fulfilled, state => {
        state.refundLoading = 'fulfilled'
      })
      .addCase(requestMobileOrderLineRefund.pending, state => {
        state.refundLoading = 'pending'
      })
      .addCase(requestMobileOrderLineRefund.rejected, state => {
        state.refundLoading = 'rejected'
      })

      .addCase(fetchMobileOrderReceipt.fulfilled, state => {
        state.affiliateOrderReceiptLoading = 'fulfilled'
      })
      .addCase(fetchMobileOrderReceipt.pending, state => {
        state.affiliateOrderReceiptLoading = 'pending'
      })
      .addCase(fetchMobileOrderReceipt.rejected, state => {
        state.affiliateOrderReceiptLoading = 'rejected'
      })

      /** WalttiPOS **/
      .addCase(fetchWalttiPosOrder.fulfilled, (state, { payload }) => {
        state.singleWalttiPosOrder = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchWalttiPosOrder.pending, state => {
        state.singleWalttiPosOrder = initialState.singleWalttiPosOrder
        state.loading = 'pending'
      })
      .addCase(fetchWalttiPosOrder.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchWalttiPosOrderList.fulfilled, (state, { payload }) => {
        state.walttiPosOrders = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.singleWalttiPosOrder = initialState.singleWalttiPosOrder // Set the single order to its initial state so that the previously displayed single order won't be briefly rendered when displaying another single order
        state.loading = 'fulfilled'
      })
      .addCase(fetchWalttiPosOrderList.pending, state => {
        state.walttiPosOrders = initialState.walttiPosOrders
        state.count = initialState.count
        state.next = initialState.next
        state.previous = initialState.previous
        state.loading = 'pending'
      })
      .addCase(fetchWalttiPosOrderList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchWalttiPosOrderReceipt.fulfilled, state => {
        state.affiliateOrderReceiptLoading = 'fulfilled'
      })
      .addCase(fetchWalttiPosOrderReceipt.pending, state => {
        state.affiliateOrderReceiptLoading = 'pending'
      })
      .addCase(fetchWalttiPosOrderReceipt.rejected, state => {
        state.affiliateOrderReceiptLoading = 'rejected'
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default orderSlice.reducer

export {
  deliverWebShopOrder,
  fetchMobileOrder,
  fetchMobileOrderList,
  fetchMobileOrderReceipt,
  fetchWalttiPosOrder,
  fetchWalttiPosOrderList,
  fetchWalttiPosOrderReceipt,
  fetchWebShopOrder,
  fetchWebShopOrderLineLog,
  fetchWebShopOrderList,
  fetchWebShopOrderReceipt,
  requestMobileOrderLineRefund,
  requestMobileOrderRefund,
  rollbackWalletTransaction
}
